import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { UtilityToolPageShell, type UtilityToolShellSkin } from '@saas-pages/shared/public/app-shell'
import branding from '../../../branding.config'
import { seoFor, type AvailableToolKey } from '@/seo/routes'
import { LocalizedProcessingBadge, useT } from '@/shared/i18n'
import {
  CATEGORY_ACCENT,
  buildCategoryLabel,
  buildTools,
  getTool,
  relatedTools,
} from '@/shared/tools/tools'

interface ToolPageShellProps {
  toolKey: AvailableToolKey
  children: ReactNode
}

const txtifyShellSkin: UtilityToolShellSkin = {
  scenariosCardClassName: 'border-dashed bg-card/70 shadow-sm',
}

export function ToolPageShell({ toolKey, children }: ToolPageShellProps) {
  const t = useT()
  const tools = buildTools(t)
  const categoryLabel = buildCategoryLabel(t)
  const tool = getTool(toolKey, tools)
  const related = relatedTools(toolKey, tools)

  return (
    <UtilityToolPageShell
      tool={tool}
      relatedTools={related}
      seo={seoFor(toolKey, t)}
      domain={branding.domain}
      ogImage={branding.ogImage}
      homeLabel={t.common.textTools}
      scenariosLabel={t.common.scenarios}
      howToLabel={t.common.howTo}
      faqLabel={t.common.faq}
      otherToolsLabel={t.common.otherTools}
      allToolsLabel={t.common.allTools}
      categoryLabel={categoryLabel[tool.category]}
      categoryClassName={CATEGORY_ACCENT[tool.category]}
      processingBadge={<LocalizedProcessingBadge variant={tool.processing} />}
      skin={txtifyShellSkin}
      renderLink={({ href, className, children }) => (
        <Link key={href} to={href} className={className}>
          {children}
        </Link>
      )}
    >
      {children}
    </UtilityToolPageShell>
  )
}
